import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, CalendarDays, ChevronDown, ChevronRight } from 'lucide-react';
import { useAppointments } from '../../contexts/AppointmentsContext';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { ROUTES } from '../../config/constants';
import AppointmentCard from './components/AppointmentCard';

/**
 * Vista principal del Calendario.
 * Dos secciones:
 *  - Próximas: pendientes con scheduledAt >= ahora, ascendente.
 *  - Pasadas: completadas + canceladas + pendientes vencidas, descendente
 *    (collapsable, default cerrada, limit 30 — paginación TODO si crece).
 */

const PAST_LIMIT = 30;

const CalendarPage = () => {
  const { appointments, status } = useAppointments();
  const [showPast, setShowPast] = useState(false);

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const upcomingArr = [];
    const pastArr = [];

    for (const a of appointments) {
      const ms = a.scheduledAt?.toMillis?.() ?? 0;
      const isPending = !a.completedAt && !a.cancelledAt;
      if (isPending && ms >= now) {
        upcomingArr.push(a);
      } else {
        pastArr.push(a);
      }
    }

    // upcoming: ya viene asc desde el context. past: ordenar desc.
    pastArr.sort((a, b) => {
      const aMs = a.scheduledAt?.toMillis?.() ?? 0;
      const bMs = b.scheduledAt?.toMillis?.() ?? 0;
      return bMs - aMs;
    });

    return { upcoming: upcomingArr, past: pastArr.slice(0, PAST_LIMIT) };
  }, [appointments]);

  const isLoading = status === 'loading';
  const isEmpty = status === 'ready' && appointments.length === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Calendario</h1>
          <p className="text-sm text-slate-500">
            Citas, vacunas, peluquería y otros compromisos del hogar.
          </p>
        </div>
        <Button
          as={Link}
          to={ROUTES.APPOINTMENT_NEW}
          variant="primary"
          size="sm"
          leftIcon={Plus}
        >
          Nueva
        </Button>
      </div>

      {isLoading && (
        <div className="flex flex-col gap-2">
          <div className="h-24 animate-pulse rounded-2xl bg-slate-100" />
          <div className="h-24 animate-pulse rounded-2xl bg-slate-100" />
        </div>
      )}

      {isEmpty && (
        <Card>
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CalendarDays className="h-10 w-10 text-slate-300" aria-hidden="true" />
            <div>
              <p className="font-medium text-slate-800">Sin citas agendadas</p>
              <p className="mt-1 text-sm text-slate-500">
                Acá vas a ver lo que tenés agendado para tus mascotas.
              </p>
            </div>
            <Button as={Link} to={ROUTES.APPOINTMENT_NEW} variant="primary" leftIcon={Plus}>
              Agendar la primera
            </Button>
          </div>
        </Card>
      )}

      {!isLoading && !isEmpty && (
        <>
          <section>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Próximas ({upcoming.length})
            </h2>
            {upcoming.length === 0 ? (
              <p className="rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-500">
                No tenés citas próximas. Tocá "Nueva" para agendar.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {upcoming.map((a) => (
                  <AppointmentCard key={a.id} appointment={a} />
                ))}
              </div>
            )}
          </section>

          {past.length > 0 && (
            <section>
              <button
                type="button"
                onClick={() => setShowPast((s) => !s)}
                className="flex w-full items-center justify-between rounded-xl px-2 py-2 text-left transition-colors hover:bg-slate-100"
                aria-expanded={showPast}
              >
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Pasadas ({past.length}
                  {past.length === PAST_LIMIT ? '+' : ''})
                </span>
                {showPast ? (
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                )}
              </button>
              {showPast && (
                <div className="mt-2 flex flex-col gap-2">
                  {past.map((a) => (
                    <AppointmentCard key={a.id} appointment={a} />
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default CalendarPage;
